import { useState, useEffect, useRef ,useMemo  } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Avatar,
    Chip,
    Divider,
    CircularProgress,
    IconButton,
    Tooltip,
    Dialog,
    Paper,
    Snackbar,
    Alert,
    Typography,
    Checkbox,
    InputAdornment,
    TextField
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import UnitMaster from './UnitMaster';
import { useAuth } from '../context/AuthContext';
import NoDataComponent from '../components/NoDataComponent';
import CustomNoDataOverlay from '../components/CustomNoDataOverlay';
import { useTheme, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DescriptionIcon from '@mui/icons-material/Description';

function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#1abc9c', '#3f51b5', '#e91e63', '#795548', '#00bcd4'];
    return colors[Math.abs(hash) % colors.length];
}
function getInitials(name) {
    if (!name) return 'U';
    return name.trim().charAt(0).toUpperCase();
}

// ── UnitModule ────────────────────────────────────────────────────────────────
// Lists units of measurement (e.g., KG, PCS, MTR) for the company.
// Each unit has a code and a description. Used when adding/editing products.
const UnitModule = ({ companyId, connectionString, mode, setMode }) => {

    const API = `${connectionString}/api`;

    const [data, setData] = useState([]);
    const [units, setUnits] = useState([]);   // ✅ REQUIRED BY UnitMaster
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { user } = useAuth();
    const role = user?.UserCategory;

    // dialog state
    const [openDialog, setOpenDialog] = useState(false);
    // const [mode, setMode] = useState('comp');
    const [editingId, setEditingId] = useState(null);
    const [selectionModel, setSelectionModel] = useState([]);
    const selectedIdsRef = useRef([]);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [totalCount, setTotalCount] = useState(0);
    const [mobileSearchQuery, setMobileSearchQuery] = useState('');
    const [selectAll, setSelectAll] = useState(false);


    const filteredData = useMemo(() => {
    if (!isMobile) return data;          // desktop untouched

    if (!mobileSearchQuery.trim()) return data;

    const q = mobileSearchQuery.toLowerCase();

    return data.filter(row =>
        String(row.Unit || '').toLowerCase().includes(q) ||
        String(row.UnitDescription || '').toLowerCase().includes(q)
    );
}, [data, mobileSearchQuery, isMobile]);


    const toggleSelectAll = () => {
    if (selectAll) {
        setSelectionModel([]);
        selectedIdsRef.current = [];
        setSelectAll(false);
    } else {
        const source = isMobile ? filteredData : data;
        const allIds = source.map(item => item.id);

        setSelectionModel(allIds);
        selectedIdsRef.current = allIds;
        setSelectAll(true);
    }
};


    // ================= FETCH GRID DATA =================
    const fetchData = async () => {
        setLoading(true);
        try {
            const { data: response } = await axios.get(`${API}/unit/search`, {
                params: { companyId, mode: 'search' },
                validateStatus: (status) => status >= 200 && status < 400,
            });

            // const transformed =
            //     response?.map((item, index) => ({
            //         ...item,
            //         id: Number(item.UnitId) || index
            //     })) || [];

            const transformed = Array.isArray(response)
                ? response.map((item, index) => ({
                    ...item,
                    id: Number(item.UnitId) || index
                }))
                : [];

            setData(transformed);
        } catch (err) {
            setSnackbar({
                open: true,
                message: 'Failed to fetch unit data',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // ================= FETCH UNIT LIST (FOR UnitMaster) =================
    const Hlp_Fn_Unit = async () => {
        try {
            const { data } = await axios.get(`${API}/unit/search`, {
                params: { companyId, mode: 'search' }
            });

            const transformed =
                Array.isArray(data)
                    ? data.map(u => ({
                        value: u.Unit
                    }))
                    : [];

            setUnits(transformed);
        } catch (err) {
            console.error('Unit lookup failed', err);
            setUnits([]);
        }
    };

    useEffect(() => {
        if (companyId) {
            fetchData();
            Hlp_Fn_Unit();   // ✅ SAME PATTERN AS ProductMaster
        }
    }, [companyId]);

    // ================= HANDLERS =================
    const handleAdd = () => {
        setMode('comp');
        setEditingId(null);
        setOpenDialog(true);
    };

    const handleEdit = (row) => {
        setMode('edit');
        setEditingId(row.id);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditingId(null);
        fetchData();
        Hlp_Fn_Unit();     // ✅ REFRESH UNITS AFTER SAVE
    };

    // const handleDelete = async (id) => {
    //     if (!window.confirm('Are you sure you want to delete this unit?')) return;

    //     try {
    //         await axios.post(`${API}/unit/delete`, {
    //             objAryId: [id]
    //         });

    //         setSnackbar({
    //             open: true,
    //             message: 'Unit deleted successfully',
    //             severity: 'success'
    //         });

    //         fetchData();
    //         Hlp_Fn_Unit();
    //     } catch {
    //         setSnackbar({
    //             open: true,
    //             message: 'Failed to delete unit',
    //             severity: 'error'
    //         });
    //     }
    // };

    // ================= COLUMNS =================

    const handleDelete = async (ids) => {
        const numericIds = ids.map(Number);
        if (!numericIds.length) return;

        // if (!window.confirm(`Are you sure you want to delete ${numericIds.length} tax record(s)?`))
        //     return;
        setConfirmOpen(false)

        try {
            // await axios.post(`${API}/unit/delete`, {
            //     objAryId: numericIds,
            //     CompId: companyId
            // });

            // setSnackbar({
            //     open: true,
            //     message: 'Unit deleted successfully',
            //     severity: 'success'
            // });

            const response = await axios.post(`${API}/unit/delete`, {
                objAryId: numericIds,
                CompId: companyId
            });

            const { deletedCount, skippedCount } = response.data;

            if (deletedCount > 0) {
                setSnackbar({
                    open: true,
                    message: "Deleted Successfully",
                    severity: "success"
                });
            }
            setSelectionModel([]);
            selectedIdsRef.current = [];
            fetchData();
            Hlp_Fn_Unit();
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;
            // 🔴 Case: 400 + referenced IDs (business rule failure)
            if (status === 400 && data?.referencedIds?.length > 0) {
                setSnackbar({
                    open: true,
                    message: "Cannot delete the selected item(s) because they are referenced in other records.",
                    severity: "warning"
                });

            } else if (status === 400 && data?.message) {
                // 🔴 400 but no referencedIds array (generic validation)
                setSnackbar({
                    open: true,
                    message: data.message,
                    severity: "warning"
                });
            } else {
                // 🔴 unexpected / server error
                setSnackbar({
                    open: true,
                    message: "Delete failed due to an unexpected error.",
                    severity: "error"
                });
            }
        }
    };

    const columns = [
        {
            field: 'Unit',
            headerName: 'Unit',
            flex: 1,
            renderCell: (params) => (
                <Typography
                    sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        textDecoration: 'underline'
                    }}
                    onClick={(e) => {
                        e.stopPropagation(); // prevent row selection
                        handleEdit(params.row);
                    }}
                >
                    {params.value}
                </Typography>
            )
        },
        { field: 'UnitDescription', headerName: 'Description', flex: 2 },
        // {
        //     field: 'actions',
        //     headerName: 'Actions',
        //     width: 120,
        //     sortable: false,
        //     filterable: false,
        //     renderCell: (params) => (
        //         <Box>
        //             <Tooltip title="Edit">
        //                 <IconButton onClick={() => handleEdit(params.row)}>
        //                     <EditIcon fontSize="small" />
        //                 </IconButton>
        //             </Tooltip>
        //             <Tooltip title="Delete">
        //                 <IconButton
        //                     color="error"
        //                     onClick={() => handleDelete(params.row.id)}
        //                 >
        //                     <DeleteIcon fontSize="small" />
        //                 </IconButton>
        //             </Tooltip>
        //         </Box>
        //     )
        // }
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isRowSelected = selectedIdsRef.current.includes(params.row.id);

                return (
                    <Box>
                        {/* DELETE (disabled if row not selected) */}
                        <Tooltip
                            title={
                                isRowSelected
                                    ? "Delete selected"
                                    : "Select this row to enable delete"
                            }
                        >
                            {/* span REQUIRED for disabled tooltip */}
                            <span>
                                <IconButton
                                    color="error"
                                    disabled={!isRowSelected}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isRowSelected) return;

                                        const idsToDelete =
                                            selectedIdsRef.current.length > 0
                                                ? selectedIdsRef.current
                                                : [params.row.id];

                                        setPendingDeleteIds(idsToDelete);
                                        setConfirmOpen(true);
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            }
        }


    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const renderMobileCards = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (filteredData.length === 0) {
            return <CustomNoDataOverlay />;
        }

        return (
            <>
                {filteredData.map((row) => {
                    const isSelected = selectionModel.includes(row.id);
                    const avatarColor = stringToColor(row.Unit || 'U');
                    const initials = getInitials(row.Unit);

                    return (
                        <Card key={row.id} sx={{ mb: 1.5, borderRadius: 2, boxShadow: 1, overflow: 'hidden', transition: '0.2s', '&:hover': { boxShadow: 3 } }}>
                            <Box sx={{ height: 5, background: `linear-gradient(90deg, ${avatarColor} 0%, ${avatarColor}88 100%)` }} />
                            <CardContent sx={{ py: 1.5, px: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                    {role !== 'User' && (
                                        <Checkbox size="small" checked={isSelected} onChange={() => {
                                            const updated = isSelected
                                                ? selectionModel.filter(id => id !== row.id)
                                                : [...selectionModel, row.id];
                                            setSelectionModel(updated);
                                            selectedIdsRef.current = updated;
                                        }} sx={{ p: 0.5, mr: 0.5, flexShrink: 0 }} />
                                    )}
                                    <Avatar sx={{ bgcolor: avatarColor, color: '#fff', width: 38, height: 38, fontWeight: 700, mr: 1.5, flexShrink: 0, fontSize: '1rem' }}>
                                        {initials}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => handleEdit(row)}>
                                            {row.Unit}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Divider sx={{ my: 0.8 }} />
                                <Box sx={{ mt: 0.8, display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                                    {row.UnitDescription && (
                                        <Chip variant="outlined" icon={<DescriptionIcon />} label={row.UnitDescription} size="small" sx={{ height: 22, fontSize: '0.75rem' }} />
                                    )}
                                </Box>
                            </CardContent>
                        </Card>

                    );
                })}

                {/* Pagination */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                        py: 2
                    }}
                >
                    <Button variant="outlined" size="small" startIcon={<NavigateBeforeIcon />}
                        disabled={paginationModel.page === 0}
                        onClick={() => setPaginationModel((p) => ({ ...p, page: Math.max(0, p.page - 1) }))}>
                        Previous
                    </Button>

                    <Typography variant="body2">
                        Page {paginationModel.page + 1} of{' '}
                        {Math.ceil(totalCount / paginationModel.pageSize) || 1}
                    </Typography>

                    <Button variant="outlined" size="small" endIcon={<NavigateNextIcon />}
                        disabled={(paginationModel.page + 1) * paginationModel.pageSize >= totalCount}
                        onClick={() => setPaginationModel((p) => ({ ...p, page: p.page + 1 }))}>
                        Next
                    </Button>
                </Box>
            </>
        );
    };

    return (
        <Box>
            {/* ── Module Header: gradient strip + title + Add button ─────────────── */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)', borderRadius: '6px 6px 0 0' }} />
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                        Units of Measurement
                    </Typography>
                    <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleAdd}>
                        Add Unit
                    </Button>
                </Paper>
            </Box>
            {data.length === 0 ? <NoDataComponent /> :
                isMobile ? (
                    <>

                        <Box sx={{ mt: 1, mb: 2, px: 1 }}>
                            <TextField fullWidth size="small" placeholder="Search Unit..." value={mobileSearchQuery} onChange={(e) => setMobileSearchQuery(e.target.value)} variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        {role !== 'User' && selectionModel.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }} >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox checked={selectAll} onChange={toggleSelectAll} size="small" />
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                        {selectionModel.length} selected
                                    </Typography>
                                </Box>
                                <Tooltip title="Delete selected">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();

                                            // ✅ pass selected ids
                                            setPendingDeleteIds(selectionModel);
                                            setConfirmOpen(true);
                                        }}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>

                                </Tooltip>
                            </Box>
                        )}

                        <Box>{renderMobileCards()}</Box>
                    </>
                ) :
                    <Box sx={{ height: 400 }}>
                        <DataGrid
                            rows={data}
                            columns={columns}
                            pageSize={5}
                            rowsPerPageOptions={[5, 10, 25]}
                            checkboxSelection
                            disableRowSelectionOnClick
                            rowSelectionModel={selectionModel}
                            onRowSelectionModelChange={(newSelection) => {
                                const numeric = newSelection.map(Number);
                                setSelectionModel(numeric);
                                selectedIdsRef.current = numeric;
                            }}
                            pagination
                            sx={{
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },

                                // Background styling
                                '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },

                                // Row styling
                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },

                                // Selected row styling (with !important to override default MUI)
                                '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },

                                // Cell content alignment
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center'
                                },
                            }}
                            slots={{
                                noResultsOverlay: CustomNoDataOverlay
                            }}
                        />
                    </Box>
            }


            {/* ✅ REUSED UNIT MASTER */}
            <Dialog open={openDialog} maxWidth="sm" fullWidth>
                <UnitMaster
                    mode={mode}
                    id={editingId}
                    units={units}                 // ✅ NOW CORRECTLY POPULATED
                    connectionString={connectionString}
                    onPopupCancel={handleClose}
                    companyId={companyId}
                />
            </Dialog>


            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => { setSnackbar(prev => ({ ...prev, open: false })) }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => { setSnackbar(prev => ({ ...prev, open: false })) }}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* ── Delete Confirmation Dialog ────────────────────────────────────────── */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)' }} />
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>
                        Confirm Deletion
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">
                            {pendingDeleteIds.length}
                        </Typography>
                        {' '}unit record(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} variant="outlined" color="inherit" size="small">Cancel</Button>
                    <Button color="error" variant="contained" size="small" startIcon={<DeleteIcon />} onClick={() => handleDelete(pendingDeleteIds)}>Delete</Button>
                </Box>
            </Dialog>
        </Box>
    );
};

export default UnitModule;
